// Required pitch-deck fields to move Taken → To Pitch(Team) or To Pitch(Team) → To Pitch(Editorial).
// Slot-3 claim / methods / similar stay optional. Save is never gated on this list.
import { KEY_REVIEWER_QUESTION_KEY } from "./contracts.js";

export type RequiredPitchKind = "text" | "stars";

export interface RequiredPitchField {
  key: string;
  label: string;
  kind: RequiredPitchKind;
}

export const REQUIRED_PITCH_FIELDS: readonly RequiredPitchField[] = [
  { key: "Research Q1", label: "Research Question 1", kind: "text" },
  { key: "Main Claim 1", label: "Main Claim 1", kind: "text" },
  { key: "Claim 1 Rating", label: "Claim 1 Rating", kind: "stars" },
  { key: "Claim 1 Rating Confidence", label: "Claim 1 Rating Confidence", kind: "stars" },
  { key: "Research Q2", label: "Research Question 2", kind: "text" },
  { key: "Main Claim 2", label: "Main Claim 2", kind: "text" },
  { key: "Claim 2 Rating", label: "Claim 2 Rating", kind: "stars" },
  { key: "Claim 2 Rating Confidence", label: "Claim 2 Rating Confidence", kind: "stars" },
  { key: "Strength of Evidence", label: "Overall Strength", kind: "stars" },
  { key: "Potential Limitation", label: "Potential Limitations", kind: "text" },
  { key: "Method Used 1", label: "Methods used", kind: "text" },
  { key: "Method Used 2", label: "Methods used (2)", kind: "text" },
  { key: KEY_REVIEWER_QUESTION_KEY, label: "Key reviewer question", kind: "text" },
  { key: "Priority(novelty)", label: "Novelty (stars)", kind: "stars" },
  { key: "Priority(novelty) Note", label: "Novelty — short note", kind: "text" },
  { key: "Priority(importance)", label: "Importance (stars)", kind: "stars" },
  { key: "Priority(importance) Note", label: "Importance — short note", kind: "text" },
  { key: "Priority(urgency)", label: "Urgency (stars)", kind: "stars" },
  { key: "Priority(urgency) Note", label: "Urgency — short note", kind: "text" },
  { key: "Priority(utility)", label: "Utility of review (stars)", kind: "stars" },
  { key: "Priority(utility) Note", label: "Utility of review — short note", kind: "text" },
  { key: "Reviewer Profile(discipline)", label: "Reviewer training / education", kind: "text" },
  { key: "Reviewer Profile(method expertise)", label: "Minimum reviewer profile", kind: "text" },
  { key: "Reviewer Profile(topical expertise)", label: "Additional reviewer characteristics", kind: "text" },
];

const MD_LINK = /\[([^\]]*)\]\(([^()\s]+)\)/;
const BARE_URL = /(https?:\/\/\S+|www\.\S+)/i;

function parseSimilarArticles(raw: string): { title: string; url: string }[] {
  return raw
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const md = line.match(MD_LINK);
      if (md) return { title: md[1]!.trim(), url: md[2]! };
      const bare = line.match(BARE_URL);
      if (bare) {
        const url = bare[1]!.replace(/[>),.;\]]+$/, "");
        const title = line
          .replace(bare[1]!, "")
          .replace(/[<>[\]():•|–—-]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        return { title, url };
      }
      return { title: line, url: "" };
    });
}

function textFilled(v: unknown): boolean {
  return String(v ?? "").trim().length > 0;
}

function starFilled(v: unknown): boolean {
  const n = Number.parseInt(String(v ?? ""), 10);
  return n >= 1 && n <= 5;
}

function similarSlotFilled(row: { title: string; url: string } | undefined): boolean {
  return !!row && textFilled(row.title) && textFilled(row.url);
}

/** Labels of required fields that are still empty. Empty array = ready to advance. */
export function missingRequiredPitchFields(pitch: Record<string, unknown>): string[] {
  const missing: string[] = [];
  for (const f of REQUIRED_PITCH_FIELDS) {
    const ok = f.kind === "stars" ? starFilled(pitch[f.key]) : textFilled(pitch[f.key]);
    if (!ok) missing.push(f.label);
  }
  const articles = parseSimilarArticles(String(pitch["Nearest Neighbor Papers"] ?? ""));
  if (!similarSlotFilled(articles[0])) missing.push("Similar Article 1");
  if (!similarSlotFilled(articles[1])) missing.push("Similar Article 2");
  return missing;
}

/** Complete required pitch for tests / fixtures. Slot-3 fields omitted on purpose. */
export function completeRequiredPitch(): Record<string, string> {
  const pitch: Record<string, string> = {
    "Nearest Neighbor Papers": "[Article one](https://example.edu/one)\n[Article two](https://example.edu/two)",
  };
  for (const f of REQUIRED_PITCH_FIELDS) {
    pitch[f.key] = f.kind === "stars" ? "3" : `filled ${f.label}`;
  }
  return pitch;
}
